/**
 * Tech-debt #4 — Reservation family (ReserveNow / CancelReservation) extracted
 * from csmsCallHandler. Both OCPP versions. Owns the auto-expiry lifecycle
 * (tech-debt #3): a reservation drives a connector to Reserved and, when it
 * lapses, reverts it + (v2.0.1) emits ReservationStatusUpdate(Expired).
 */
import {
  Availability,
  ConnectorStatus,
  type ConnectorId,
  type StationId,
} from '@/domain/types';
import { errorMessage } from '@/lib/errorMessage';
import type { OcppAdapter } from '@/protocol/OcppAdapter';
import { OcppV201Adapter } from '@/protocol/v201/OcppV201Adapter';
import { csmsSession } from '../csmsSessionState';
import { useStationStore } from '../useStationStore';
import type { CsmsCtx, CsmsEvent } from './context';

export function handleReserveNow(event: CsmsEvent<'ReserveNow'>, ctx: CsmsCtx): void {
  const { adapter, dispatcher, stationId, messageId } = ctx;
  // ReserveNowStatus enum: { Accepted, Faulted, Occupied, Rejected,
  // Unavailable }. Resolves a concrete connector (connectorId 0 = "any" →
  // first Available one), sets it Reserved + fires StatusNotification, and
  // records the reservation so CancelReservation can find it.
  const store = useStationStore.getState();
  const all = [...store.connectors.values()];
  const target =
    event.connectorId !== 0
      ? store.connectors.get(event.connectorId)
      : all.find(
          (c) =>
            c.availability === Availability.Operative &&
            c.activeTransactionId === null &&
            c.status === ConnectorStatus.Available,
        );
  if (!target) {
    // Unknown connector, or no free connector for an "any" reservation.
    dispatcher.reply(messageId, { status: event.connectorId !== 0 ? 'Rejected' : 'Occupied' });
    return;
  }
  if (target.availability === Availability.Inoperative) {
    dispatcher.reply(messageId, { status: 'Unavailable' });
    return;
  }
  if (target.status === ConnectorStatus.Faulted) {
    dispatcher.reply(messageId, { status: 'Faulted' });
    return;
  }
  if (
    target.activeTransactionId !== null ||
    (target.status !== ConnectorStatus.Available && target.status !== ConnectorStatus.Reserved)
  ) {
    dispatcher.reply(messageId, { status: 'Occupied' });
    return;
  }
  csmsSession.addReservation(stationId, {
    reservationId: event.reservationId,
    connectorId: Number(target.id),
    idTag: event.idTag,
    expiryDate: event.expiryDate,
  });
  // Tech-debt #3 — auto-expire: when the reservation lapses, revert the
  // connector to Available + (v2.0.1) send ReservationStatusUpdate(Expired).
  scheduleReservationExpiry(stationId, event.reservationId, target.id, event.expiryDate, adapter);
  const next = new Map(store.connectors);
  next.set(target.id, { ...target, status: ConnectorStatus.Reserved });
  useStationStore.setState({ connectors: next });
  void adapter.statusNotification({ connectorId: target.id, status: ConnectorStatus.Reserved })
    .response;
  dispatcher.reply(messageId, { status: 'Accepted' });
}

export function handleCancelReservation(
  event: CsmsEvent<'CancelReservation'>,
  ctx: CsmsCtx,
): void {
  const { adapter, dispatcher, stationId, messageId } = ctx;
  // CancelReservationStatus enum: { Accepted, Rejected }.
  const rec = csmsSession.getReservation(stationId, event.reservationId);
  if (!rec) {
    dispatcher.reply(messageId, { status: 'Rejected' });
    return;
  }
  csmsSession.removeReservation(stationId, event.reservationId);
  revertReservedConnector(rec.connectorId as ConnectorId, adapter);
  dispatcher.reply(messageId, { status: 'Accepted' });
}

/**
 * Tech-debt #3 — setTimeout delays are 32-bit; a delay past this is clamped, so
 * a reservation far in the future simply never fires in a short-lived session
 * (correct — a >24-day reservation is unrealistic), instead of the legacy
 * footgun where an over-large delay wraps to ~1 ms.
 */
const MAX_TIMER_MS = 2_147_483_647;

/** Schedule a reservation to auto-expire at `expiryDate`. */
function scheduleReservationExpiry(
  stationId: StationId,
  reservationId: number,
  connectorId: ConnectorId,
  expiryDate: string,
  adapter: OcppAdapter,
): void {
  const ms = new Date(expiryDate).getTime() - Date.now();
  if (Number.isNaN(ms)) return; // unparseable expiry → no timer
  const delay = Math.min(Math.max(ms, 0), MAX_TIMER_MS);
  const handle = setTimeout(() => {
    void expireReservation(stationId, reservationId, connectorId, adapter);
  }, delay);
  csmsSession.setReservationTimer(stationId, reservationId, handle);
}

/** Fired when a reservation lapses: revert the connector + notify (v2.0.1). */
async function expireReservation(
  stationId: StationId,
  reservationId: number,
  connectorId: ConnectorId,
  adapter: OcppAdapter,
): Promise<void> {
  // No-op if it was cancelled or consumed by a transaction in the meantime.
  const rec = csmsSession.removeReservation(stationId, reservationId);
  if (!rec) return;
  revertReservedConnector(connectorId, adapter);
  if (adapter instanceof OcppV201Adapter) {
    try {
      await adapter.reservationStatusUpdate({
        reservationId,
        reservationUpdateStatus: 'Expired',
      }).response;
    } catch (err) {
      useStationStore.setState({ lastError: errorMessage(err) });
    }
  }
}

/**
 * If `connectorId` is still Reserved, revert it to Available and emit the
 * matching StatusNotification. Shared by CancelReservation and auto-expiry.
 */
function revertReservedConnector(connectorId: ConnectorId, adapter: OcppAdapter): void {
  const store = useStationStore.getState();
  const c = store.connectors.get(connectorId);
  if (c && c.status === ConnectorStatus.Reserved) {
    const next = new Map(store.connectors);
    next.set(c.id, { ...c, status: ConnectorStatus.Available });
    useStationStore.setState({ connectors: next });
    void adapter.statusNotification({ connectorId: c.id, status: ConnectorStatus.Available })
      .response;
  }
}
